import { createContext, useCallback, useContext, useMemo, useState } from "react";
import en from "./en";
import es from "./es";

const STORAGE_KEY = "showcase:locale";
const MESSAGES = { en, es };

const LocaleContext = createContext(null);

function getNested(obj, path) {
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
}

function interpolate(str, vars) {
  if (!vars || typeof str !== "string") return str;
  return str.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : `{${k}}`));
}

export function LocaleProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === "en" || saved === "es") return saved;
    } catch {
      /* ignore */
    }
    return "en";
  });
  const [sparkKey, setSparkKey] = useState(0);

  const setLocale = useCallback((next) => {
    if (next !== "en" && next !== "es") return;
    setLocaleState(next);
    setSparkKey((k) => k + 1);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const t = useCallback(
    (key, vars) => {
      const val = getNested(MESSAGES[locale], key) ?? getNested(MESSAGES.en, key) ?? key;
      return interpolate(val, vars);
    },
    [locale]
  );

  const value = useMemo(
    () => ({ locale, setLocale, t, sparkKey }),
    [locale, setLocale, t, sparkKey]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within LocaleProvider");
  return ctx;
}
